import { SelectOptionGroups } from '@factorialco/deprecated-design-system'
import {
  BookkeepersCompanyIncidenceMessageStatFragment,
  BookkeepersCompanyIncidenceStatFragment,
} from 'generated/resources/graphql'

import createComputed from 'modules/core/hooks/createComputed'

import { INCIDENCE_STATUSES } from './Status'
import {
  CONTRACT_CHANGE_TYPES,
  EMPLOYEE_CHANGE_TYPES,
  LEAVE_CHANGE_TYPES,
  MANUAL_CHANGE_TYPES,
} from './Types'
import { typeFilterLabelTranslations } from './dynamicTranslations'

interface IncidenceStatsCount {
  additional_compensation_change: number
  address: number
  all_contracts: number
  all_hiring: number
  all_manual_incidences: number
  all_terminations: number
  bank: number
  contract: number
  cost_center: number
  custom_leave: number
  email: number
  gender: number
  id: string
  irpf: number
  legal_entity: number
  name: number
  nationality: number
  other_contract_updates: number
  parental: number
  residence: number
  salary_update: number
  sick: number
  work_schedule_update: number
  workplace: number
}

const filterOptions = () => {
  return {
    hiring: {
      all_hiring: [EMPLOYEE_CHANGE_TYPES.hiring],
    },
    termination: {
      all_terminations: [EMPLOYEE_CHANGE_TYPES.termination],
    },
    absence: {
      all_leaves: [
        LEAVE_CHANGE_TYPES.sick,
        LEAVE_CHANGE_TYPES.parental,
        LEAVE_CHANGE_TYPES.custom_leave,
      ],
      sick: [LEAVE_CHANGE_TYPES.sick],
      parental: [LEAVE_CHANGE_TYPES.parental],
      custom_leave: [LEAVE_CHANGE_TYPES.custom_leave],
    },
    contract: {
      all_contracts: [CONTRACT_CHANGE_TYPES.contract],
    },
    personal_data: {
      all_personal_data: [
        EMPLOYEE_CHANGE_TYPES.id,
        EMPLOYEE_CHANGE_TYPES.name,
        EMPLOYEE_CHANGE_TYPES.irpf,
        EMPLOYEE_CHANGE_TYPES.bank,
        EMPLOYEE_CHANGE_TYPES.gender,
        EMPLOYEE_CHANGE_TYPES.address,
        EMPLOYEE_CHANGE_TYPES.residence,
        EMPLOYEE_CHANGE_TYPES.workplace,
        EMPLOYEE_CHANGE_TYPES.nationality,
        EMPLOYEE_CHANGE_TYPES.email,
        EMPLOYEE_CHANGE_TYPES.phone_number,
      ],
      cost_center: [EMPLOYEE_CHANGE_TYPES.cost_center],
      id: [EMPLOYEE_CHANGE_TYPES.id],
      name: [EMPLOYEE_CHANGE_TYPES.name],
      legal_entity: [EMPLOYEE_CHANGE_TYPES.legal_entity],
      irpf: [EMPLOYEE_CHANGE_TYPES.irpf],
      bank: [EMPLOYEE_CHANGE_TYPES.bank],
      gender: [EMPLOYEE_CHANGE_TYPES.gender],
      address: [EMPLOYEE_CHANGE_TYPES.address],
      residence: [EMPLOYEE_CHANGE_TYPES.residence],
      workplace: [EMPLOYEE_CHANGE_TYPES.workplace],
      nationality: [EMPLOYEE_CHANGE_TYPES.nationality],
      email: [EMPLOYEE_CHANGE_TYPES.email],
      phone_number: [EMPLOYEE_CHANGE_TYPES.phone_number],
    },
    manual_incidence: {
      all_manual_incidences: [MANUAL_CHANGE_TYPES.manual_incidence],
    },
  }
}

export const incidenceTypeAsGroupedSelectOptions = (
  incidenceStatsCount: Record<string, number>,
  custom_leave_name: string | undefined
) => {
  const result: SelectOptionGroups = {}

  Object.entries(filterOptions()).forEach(([parentFilter, data]) => {
    const translatedParent = typeFilterLabelTranslations(parentFilter)

    const childFilter = Object.entries(data).map(([key, value]) => {
      const count = incidenceStatsCount[key] ?? 0
      const activeCustomLeaveFiltersCount = custom_leave_name?.split(',').length ?? 0

      return {
        value: value,
        label: typeFilterLabelTranslations(key) + (count > 0 ? ` (${count})` : ''),
        activeFilters: key === 'custom_leave' ? activeCustomLeaveFiltersCount : 0,
      }
    })
    result[translatedParent] = childFilter
  })

  return result
}

export const getIncidencesUnreadMessagesCount = createComputed(
  (stats: BookkeepersCompanyIncidenceMessageStatFragment[], status?: string) => {
    let incidencesUnreadMessagesCount = 0
    if (status) {
      const filteredStats = stats.filter(
        (model: BookkeepersCompanyIncidenceMessageStatFragment) => status === model.status
      )
      filteredStats.forEach((model: BookkeepersCompanyIncidenceMessageStatFragment) => {
        incidencesUnreadMessagesCount += calculateTotal(model)
      })
    } else {
      stats.forEach((model: BookkeepersCompanyIncidenceMessageStatFragment) => {
        incidencesUnreadMessagesCount += calculateTotal(model)
      })
    }

    return { incidencesUnreadMessagesCount }
  }
)

export const calculateTotal = (
  model: BookkeepersCompanyIncidenceMessageStatFragment | BookkeepersCompanyIncidenceStatFragment
) => {
  const { status: _status, id: _id, ...metadata } = model
  return Object.values(metadata).reduce((acc: number, curr) => {
    const currType = typeof curr
    const value: number = currType === 'number' ? Number(curr) : 0
    return acc + value
  }, 0)
}

export const calculateTypeTotal = (
  model: BookkeepersCompanyIncidenceMessageStatFragment | BookkeepersCompanyIncidenceStatFragment
) => {
  const { status: _status, id: _id, ...metadata } = model
  const result: Record<string, number> = {}
  Object.entries(metadata).forEach(([key, value]) => {
    const valueType = typeof value
    const parsedValue: number = valueType === 'number' ? Number(value) : 0
    if (parsedValue !== 0) {
      result[key] = parsedValue
    }
  })

  return result
}

export const getIncidenceStatsCount = createComputed(
  (stats: BookkeepersCompanyIncidenceStatFragment[], status?) => {
    const parsedStatus = !status ? Object.values(INCIDENCE_STATUSES) : status.split(',')
    const record: Record<string, number> = {}
    const result: IncidenceStatsCount[] = stats
      .filter((stat: BookkeepersCompanyIncidenceStatFragment) =>
        parsedStatus?.includes(stat.status)
      )
      .map((stat: BookkeepersCompanyIncidenceStatFragment) => {
        const {
          status: _status,
          id: id,
          customLeave,
          legalEntity,
          additionalCompensationChange,
          hiring,
          contract,
          termination,
          manualIncidence,
          otherContractUpdates,
          salaryUpdate,
          costCenter,
          workScheduleUpdate,
          ...otherProperties
        } = stat

        return {
          id: id,
          additional_compensation_change: additionalCompensationChange,
          all_hiring: hiring,
          all_contracts: contract,
          all_terminations: termination,
          all_manual_incidences: manualIncidence,
          other_contract_updates: otherContractUpdates,
          salary_update: salaryUpdate,
          work_schedule_update: workScheduleUpdate,
          cost_center: costCenter,
          custom_leave: customLeave,
          legal_entity: legalEntity,
          contract: contract,
          ...otherProperties,
        }
      })
    const incidenceStatKeys = Object.keys(result[0] ?? {})

    result.forEach((item: IncidenceStatsCount) => {
      Object.entries(item).forEach(([key, value]) => {
        const valueType = typeof value
        const parsedValue: number = valueType === 'number' ? Number(value) : 0
        if (record[key]) {
          record[key] += parsedValue
        } else {
          record[key] = parsedValue
        }
      })
    })

    const getSumByKeys = (data: Record<string, string>, source: Record<string, number>) => {
      return incidenceStatKeys.reduce((sum, sourceKey) => {
        if (Object.keys(data).includes(sourceKey)) {
          sum += source[sourceKey] ?? 0
        }

        return sum
      }, 0)
    }

    record.all_leaves = getSumByKeys(LEAVE_CHANGE_TYPES, record)
    record.all_personal_data = getSumByKeys(EMPLOYEE_CHANGE_TYPES, record)

    return record
  }
)
